// The prototype's modal (`dialog.ui-confirm-dialog`: span.ui-eyebrow, h2, body, form footer) on Radix Dialog for the focus trap.
import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import './protoDialog.css'

export interface ProtoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  eyebrow: string
  title: React.ReactNode
  children?: React.ReactNode
  /** Buttons for the right-aligned footer (the prototype's `form method="dialog"`). */
  footer?: React.ReactNode
  className?: string
  role?: 'dialog' | 'alertdialog'
}

/** prototype.css scopes every rule under #zupa-complete, so the portal lands there when the shell is mounted. */
function portalRoot(): HTMLElement | undefined {
  return typeof document === 'undefined' ? undefined : document.getElementById('zupa-complete') ?? undefined
}

export function ProtoDialog({ open, onOpenChange, eyebrow, title, children, footer, className, role = 'dialog' }: ProtoDialogProps) {
  // Radix only returns focus to its own Trigger; these dialogs open from ordinary buttons.
  const opener = React.useRef<HTMLElement | null>(null)
  if (open && !opener.current && typeof document !== 'undefined') opener.current = document.activeElement as HTMLElement | null
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal container={portalRoot()}>
        <DialogPrimitive.Overlay className="d0-dialog-backdrop" />
        <DialogPrimitive.Content
          role={role}
          aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            opener.current?.focus()
            opener.current = null
          }}
          className={['ui-confirm-dialog d0-dialog', className].filter(Boolean).join(' ')}
        >
          <span className="ui-eyebrow">{eyebrow}</span>
          <DialogPrimitive.Title asChild><h2>{title}</h2></DialogPrimitive.Title>
          {children}
          {footer ? <form onSubmit={e => e.preventDefault()}>{footer}</form> : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
